<template>
  <div class="relative">
    <q-inner-loading :showing="isLoading">
      <q-spinner-gears size="100px" color="primary" />
    </q-inner-loading>

    <!-- Error Banner -->
    <q-banner
      v-if="error"
      inline-actions
      rounded
      class="bg-gradient-to-r from-orange-500 to-red-500 text-white m-4 rounded-xl"
    >
      <q-icon name="error" color="white" size="2em" />
      <span class="ml-2 font-semibold">{{ error }}</span>
    </q-banner>

    <!-- Main Content -->
    <div class="p-6">
      <q-banner
        v-if="!isNative"
        class="bg-orange-100 text-orange-800 border border-orange-200 rounded-lg mb-6"
        rounded
        inline-actions
      >
        <div class="flex items-center gap-2">
          <q-icon name="warning" color="orange-7" />
          <span>当前为 Web 环境，原生能力不可用</span>
        </div>
      </q-banner>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Left: Property Input and List -->
        <div
          class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center"
            >
              <q-icon name="list" class="text-white text-xl" />
            </div>
            <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
              属性管理
            </h2>
          </div>

          <!-- Search Input -->
          <div class="mb-4">
            <q-input
              v-model="searchKeyword"
              outlined
              dense
              placeholder="搜索属性名称或ID..."
              bg-color="white"
              class="w-full"
            >
              <template v-slot:prepend>
                <q-icon name="search" />
              </template>
              <template v-slot:append>
                <q-icon
                  v-if="searchKeyword"
                  name="clear"
                  @click="searchKeyword = ''"
                  class="cursor-pointer"
                />
              </template>
            </q-input>
          </div>

          <!-- Load Button -->
          <div class="mb-4">
            <button
              @click="loadAllProperties"
              :disabled="loadingProperties || !isNative"
              class="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
            >
              <div class="flex items-center justify-center gap-2">
                <q-spinner-dots v-if="loadingProperties" size="16px" />
                <q-icon v-else name="refresh" />
                <span>{{
                  loadingProperties ? "加载中..." : "加载所有属性"
                }}</span>
              </div>
            </button>
          </div>

          <!-- Property List -->
          <div class="space-y-2">
            <div
              v-if="filteredProperties.length === 0"
              class="text-center text-gray-500 dark:text-gray-400 py-8"
            >
              {{
                loadingProperties
                  ? "加载中..."
                  : searchKeyword
                  ? "未找到匹配的属性"
                  : "请点击加载所有属性"
              }}
            </div>
            <div
              v-else
              class="text-xs text-gray-500 dark:text-gray-400 mb-2 px-2 flex items-center justify-between"
            >
              <span>共找到 {{ filteredProperties.length }} 个属性</span>
              <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
            </div>
            <div class="max-h-[500px] overflow-y-auto">
              <div
                v-for="prop in paginatedProperties"
                :key="prop.propertyName"
                @click="selectProperty(prop)"
                :class="
                  selectedProperty?.propertyName === prop.propertyName
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                "
                class="rounded-lg p-3 cursor-pointer transition-all duration-200 mb-2"
              >
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <span class="font-semibold">{{ prop.propertyName }}</span>
                      <q-chip
                        v-if="prop.subscribed"
                        size="sm"
                        color="green"
                        text-color="white"
                      >
                        已订阅
                      </q-chip>
                    </div>
                    <div
                      :class="
                        selectedProperty?.propertyName === prop.propertyName
                          ? 'text-xs text-blue-100'
                          : 'text-xs text-gray-600 dark:text-gray-400'
                      "
                    >
                      ID: {{ prop.propertyId }}
                    </div>
                    <div
                      v-if="prop.config"
                      :class="
                        selectedProperty?.propertyName === prop.propertyName
                          ? 'text-xs text-blue-100 mt-1'
                          : 'text-xs text-gray-600 dark:text-gray-400 mt-1'
                      "
                    >
                      Access: {{ translateAccessMode(prop.config.access) }}
                    </div>
                  </div>
                  <div class="flex items-center gap-2" @click.stop>
                    <q-btn
                      flat
                      dense
                      round
                      icon="content_copy"
                      size="sm"
                      @click="copyPropertyName(prop.propertyName)"
                      title="复制属性名称"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div
              v-if="filteredProperties.length > 0 && totalPages > 1"
              class="flex justify-center mt-4"
            >
              <q-pagination
                v-model="currentPage"
                :max="totalPages"
                :max-pages="7"
                direction-links
                boundary-links
                color="primary"
              />
            </div>
          </div>
        </div>

        <!-- Right: Property Details and Actions -->
        <div
          class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center"
            >
              <q-icon name="info" class="text-white text-xl" />
            </div>
            <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
              属性详情
            </h2>
          </div>

          <div
            v-if="!selectedProperty"
            class="text-center text-gray-500 dark:text-gray-400 py-8"
          >
            请选择一个属性查看详情
          </div>

          <div v-else class="space-y-4">
            <!-- Property Name -->
            <div
              class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700/50"
            >
              <div class="flex items-center justify-between mb-2">
                <div
                  class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                >
                  属性名称
                </div>
                <q-btn
                  flat
                  dense
                  round
                  icon="content_copy"
                  size="sm"
                  @click="copyPropertyName(selectedProperty.propertyName)"
                  title="复制"
                />
              </div>
              <div
                class="text-lg font-bold text-blue-700 dark:text-blue-300 font-mono"
              >
                {{ selectedProperty.propertyName }}
              </div>
            </div>

            <!-- Property Config -->
            <div
              v-if="selectedProperty.config"
              class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl p-4 border border-green-200 dark:border-green-700/50 space-y-2"
            >
              <div
                class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2"
              >
                属性配置
              </div>
              <div class="space-y-1 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">属性ID:</span>
                  <span
                    class="font-mono font-semibold text-green-700 dark:text-green-300"
                  >
                    {{ selectedProperty.config.propertyId }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400"
                    >访问模式:</span
                  >
                  <span
                    class="font-semibold text-green-700 dark:text-green-300"
                  >
                    {{ translateAccessMode(selectedProperty.config.access) }}
                  </span>
                </div>
                <div
                  v-if="selectedProperty.config.changeMode !== undefined"
                  class="flex justify-between"
                >
                  <span class="text-gray-600 dark:text-gray-400"
                    >变化模式:</span
                  >
                  <span
                    class="font-semibold text-green-700 dark:text-green-300"
                  >
                    {{
                      translateChangeMode(selectedProperty.config.changeMode)
                    }}
                  </span>
                </div>
                <div
                  v-if="selectedProperty.config.type"
                  class="flex justify-between"
                >
                  <span class="text-gray-600 dark:text-gray-400"
                    >属性类型:</span
                  >
                  <span
                    class="font-mono font-semibold text-green-700 dark:text-green-300"
                  >
                    {{ selectedProperty.config.type }}
                  </span>
                </div>
                <div
                  v-if="
                    selectedProperty.config.supportedAreas &&
                    selectedProperty.config.supportedAreas.length > 0
                  "
                  class="mt-2 pt-2 border-t border-green-200 dark:border-green-700/50"
                >
                  <div
                    class="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1"
                  >
                    支持的区域 ({{
                      selectedProperty.config.supportedAreas.length
                    }}):
                  </div>
                  <div class="space-y-1 max-h-32 overflow-y-auto">
                    <div
                      v-for="area in selectedProperty.config.supportedAreas"
                      :key="area.areaId"
                      class="text-xs bg-white/50 dark:bg-gray-800/50 rounded p-1"
                    >
                      <div class="font-mono">AreaId: {{ area.areaId }}</div>
                      <div
                        v-if="
                          area.minValue !== null && area.minValue !== undefined
                        "
                        class="text-gray-600 dark:text-gray-400"
                      >
                        最小值: {{ formatPropertyValue(area.minValue) }}
                      </div>
                      <div
                        v-if="
                          area.maxValue !== null && area.maxValue !== undefined
                        "
                        class="text-gray-600 dark:text-gray-400"
                      >
                        最大值: {{ formatPropertyValue(area.maxValue) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">可读:</span>
                  <span
                    :class="
                      selectedProperty.config.isReadable
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    "
                    class="font-semibold"
                  >
                    {{ selectedProperty.config.isReadable ? "是" : "否" }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">可写:</span>
                  <span
                    :class="
                      selectedProperty.config.isWritable
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    "
                    class="font-semibold"
                  >
                    {{ selectedProperty.config.isWritable ? "是" : "否" }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400"
                    >最小采样率:</span
                  >
                  <span
                    class="font-semibold text-green-700 dark:text-green-300"
                  >
                    {{ selectedProperty.config.minSampleRate }} Hz
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400"
                    >最大采样率:</span
                  >
                  <span
                    class="font-semibold text-green-700 dark:text-green-300"
                  >
                    {{ selectedProperty.config.maxSampleRate }} Hz
                  </span>
                </div>
              </div>
            </div>

            <!-- Property Value -->
            <div
              v-if="selectedProperty.value"
              class="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl p-4 border border-purple-200 dark:border-purple-700/50 space-y-2"
            >
              <div
                class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2"
              >
                属性值
              </div>
              <div class="space-y-1 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">状态:</span>
                  <span
                    :class="
                      String(selectedProperty.value.status) === 'OK' ||
                      selectedProperty.value.status === 0
                        ? 'text-green-600 dark:text-green-400'
                        : String(selectedProperty.value.status) ===
                            'TRY_AGAIN' || selectedProperty.value.status === 1
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    "
                    class="font-semibold"
                  >
                    {{ translateStatus(selectedProperty.value.status) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">值:</span>
                  <span
                    class="font-mono font-bold text-purple-700 dark:text-purple-300"
                  >
                    {{ formatPropertyValue(selectedProperty.value.value) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">时间戳:</span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ formatTimestamp(selectedProperty.value.timestamp) }}
                  </span>
                </div>
                <div
                  v-if="selectedProperty.value.onChangeFlag !== undefined"
                  class="flex justify-between"
                >
                  <span class="text-gray-600 dark:text-gray-400"
                    >变化标志:</span
                  >
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{
                      translateOnChangeFlag(selectedProperty.value.onChangeFlag)
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="space-y-2">
              <button
                @click="loadPropertyConfig"
                :disabled="loadingConfig || !isNative"
                class="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-spinner-dots v-if="loadingConfig" size="16px" />
                  <q-icon v-else name="settings" />
                  <span>获取配置</span>
                </div>
              </button>

              <button
                @click="getPropertyValue"
                :disabled="gettingProperty || !isNative"
                class="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-spinner-dots v-if="gettingProperty" size="16px" />
                  <q-icon v-else name="download" />
                  <span>获取属性值</span>
                </div>
              </button>

              <button
                v-if="!selectedProperty.subscribed"
                @click="subscribeProperty"
                :disabled="subscribing || !isNative"
                class="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-spinner-dots v-if="subscribing" size="16px" />
                  <q-icon v-else name="notifications_active" />
                  <span>订阅属性</span>
                </div>
              </button>

              <button
                v-else
                @click="unsubscribeProperty"
                :disabled="unsubscribing || !isNative"
                class="w-full px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200"
              >
                <div class="flex items-center justify-center gap-2">
                  <q-spinner-dots v-if="unsubscribing" size="16px" />
                  <q-icon v-else name="notifications_off" />
                  <span>取消订阅</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Subscribed List -->
        <div
          class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center"
            >
              <q-icon name="subscriptions" class="text-white text-xl" />
            </div>
            <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
              已订阅列表
            </h2>
          </div>

          <div
            v-if="subscribedList.length === 0"
            class="text-center text-gray-500 dark:text-gray-400 py-8"
          >
            暂无订阅数据
          </div>

          <div v-else class="space-y-3 max-h-[700px] overflow-y-auto">
            <div
              v-for="item in subscribedList"
              :key="item.propertyName"
              @click="selectProperty(item)"
              class="p-4 rounded-lg border border-emerald-200 dark:border-emerald-700/60 bg-white/70 dark:bg-gray-800/80 shadow-sm hover:shadow transition cursor-pointer"
            >
              <div class="flex items-start justify-between">
                <div>
                  <div class="font-semibold text-gray-800 dark:text-gray-100">
                    {{ item.propertyName }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ID: {{ item.propertyId }}
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <q-chip color="green" text-color="white" size="sm">订阅中</q-chip>
                  <q-btn
                    dense
                    flat
                    size="sm"
                    color="negative"
                    label="取消"
                    :loading="unsubscribingFrom === item.propertyName"
                    @click.stop="unsubscribeByName(item.propertyName)"
                  />
                </div>
              </div>
              <div class="mt-3 space-y-1 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">状态:</span>
                  <span
                    :class="
                      String(item.value?.status) === 'OK' ||
                      item.value?.status === 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-yellow-600 dark:text-yellow-400'
                    "
                    class="font-semibold"
                  >
                    {{ item.value ? translateStatus(item.value.status) : "--" }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">值:</span>
                  <span
                    class="font-mono font-bold text-emerald-700 dark:text-emerald-300"
                  >
                    {{ formatPropertyValue(item.value?.value) }}
                  </span>
                </div>
                <div
                  class="flex justify-between text-xs text-gray-500 dark:text-gray-400"
                >
                  <span>时间戳:</span>
                  <span>{{
                    item.value ? formatTimestamp(item.value.timestamp) : "--"
                  }}</span>
                </div>
                <div
                  v-if="item.value?.onChangeFlag !== undefined"
                  class="flex justify-between text-xs text-gray-500 dark:text-gray-400"
                >
                  <span>变化标志:</span>
                  <span>{{ translateOnChangeFlag(item.value.onChangeFlag) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useQuasar } from "quasar";
import { isNative } from "@/utils/capacitor";
import { Clipboard } from "@capacitor/clipboard";
import CarClient, {
  CarPropertyValue,
  formatTimestamp,
  translateAccessMode,
  translateChangeMode,
  translateStatus,
  translateOnChangeFlag,
} from "@/utils/capacitor/carPlugin";

const $q = useQuasar();

interface PropertyItem {
  propertyName: string;
  propertyId: number;
  subscribed: boolean;
  config?: {
    propertyId: number;
    propertyName: string;
    access: number; // 0 = NONE, 1 = READ, 2 = WRITE, 3 = READ_WRITE
    isReadable: boolean;
    isWritable: boolean;
    changeMode: number; // 0 = STATIC, 1 = ON_CHANGE, 2 = CONTINUOUS
    type?: string;
    supportedAreas?: Array<{
      areaId: number;
      minValue: any;
      maxValue: any;
    }>;
    minSampleRate: number;
    maxSampleRate: number;
  };
  value?: CarPropertyValue;
}

const isLoading = ref(false);
const loadingProperties = ref(false);
const loadingConfig = ref(false);
const gettingProperty = ref(false);
const subscribing = ref(false);
const unsubscribing = ref(false);
const unsubscribingFrom = ref<string | null>(null);
const error = ref<string>("");
const searchKeyword = ref<string>("");
const allProperties = ref<PropertyItem[]>([]);
const selectedProperty = ref<PropertyItem | null>(null);
const subscribedList = ref<PropertyItem[]>([]);

// 分页相关
const currentPage = ref(1);
const itemsPerPage = ref(50); // 每页显示50个属性

const filteredProperties = computed(() => {
  if (!searchKeyword.value.trim()) {
    return allProperties.value;
  }

  const keyword = searchKeyword.value.trim().toLowerCase();
  const filtered = allProperties.value.filter((prop) => {
    // 搜索属性名称（不区分大小写）
    const nameMatch = prop.propertyName.toLowerCase().includes(keyword);

    // 搜索属性ID（支持十进制）
    const idDecimal = prop.propertyId.toString().includes(keyword);

    return nameMatch || idDecimal;
  });

  return filtered;
});

// 分页后的属性列表
const paginatedProperties = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredProperties.value.slice(start, end);
});

// 总页数
const totalPages = computed(() => {
  return Math.ceil(filteredProperties.value.length / itemsPerPage.value);
});

// 监听搜索关键词变化，重置到第一页
watch(searchKeyword, () => {
  currentPage.value = 1;
});

// 监听总页数变化，确保当前页不超过总页数
watch(totalPages, (newTotalPages) => {
  if (currentPage.value > newTotalPages && newTotalPages > 0) {
    currentPage.value = newTotalPages;
  }
});

const loadAllProperties = async () => {
  if (!isNative) {
    error.value = "仅在原生环境可用";
    $q.notify({ type: "warning", message: error.value, position: "top" });
    return;
  }

  loadingProperties.value = true;
  error.value = "";
  currentPage.value = 1; // 重置到第一页

  try {
    const result = await CarClient.getAllVehicleProperties({
      includeValues: false, // 不包含值，只获取属性名和ID
    });

    if (result.properties) {
      allProperties.value = result.properties.map((prop) => ({
        propertyName: prop.propertyName,
        propertyId: prop.propertyId,
        subscribed: false,
      }));

      $q.notify({
        type: "positive",
        message: `成功加载 ${allProperties.value.length} 个属性`,
        position: "top",
      });
    }
  } catch (err: any) {
    error.value = err?.message || "加载属性失败";
    $q.notify({ type: "negative", message: error.value, position: "top" });
  } finally {
    loadingProperties.value = false;
  }
};

const copyPropertyName = async (name: string) => {
  try {
    await Clipboard.write({
      string: name,
    });
    $q.notify({
      type: "positive",
      message: `已复制: ${name}`,
      position: "top",
    });
  } catch (err) {
    console.error("复制失败:", err);
    $q.notify({
      type: "negative",
      message: "复制失败",
      position: "top",
    });
  }
};

const loadPropertyConfig = async () => {
  if (!selectedProperty.value || !isNative) return;

  loadingConfig.value = true;
  error.value = "";

  try {
    const config = await CarClient.getPropertyConfig({
      propertyName: selectedProperty.value.propertyName,
    });

    if (selectedProperty.value) {
      selectedProperty.value.config = config;
      // 更新列表中的配置
      const index = allProperties.value.findIndex(
        (p) => p.propertyName === selectedProperty.value!.propertyName
      );
      if (index !== -1) {
        allProperties.value[index].config = config;
      }
    }

    $q.notify({
      type: "positive",
      message: "配置获取成功",
      position: "top",
    });
  } catch (err: any) {
    error.value = err?.message || "获取配置失败";
    $q.notify({
      type: "negative",
      message: error.value,
      position: "top",
    });
  } finally {
    loadingConfig.value = false;
  }
};

const getPropertyValue = async () => {
  if (!selectedProperty.value || !isNative) return;

  gettingProperty.value = true;
  error.value = "";

  try {
    const result = await CarClient.getProperty({
      propertyName: selectedProperty.value.propertyName,
    });
    console.log("[BOWEN_LOG] 🚀 ~~ getPropertyValue ~~ result:", result);

    if (selectedProperty.value) {
      selectedProperty.value.value = result;
      // 更新列表中的值
      const index = allProperties.value.findIndex(
        (p) => p.propertyName === selectedProperty.value!.propertyName
      );
      if (index !== -1) {
        allProperties.value[index].value = result;
      }
    }

    const statusStr = String(result.status);
    const isOk = statusStr === "OK" || result.status === 0;
    $q.notify({
      type: isOk ? "positive" : "warning",
      message: `获取属性值${isOk ? "成功" : "完成"}，状态: ${result.status}`,
      position: "top",
    });
  } catch (err: any) {
    error.value = err?.message || "获取属性值失败";
    $q.notify({
      type: "negative",
      message: error.value,
      position: "top",
    });
  } finally {
    gettingProperty.value = false;
  }
};

const subscribeProperty = async () => {
  if (!selectedProperty.value || !isNative) return;

  const targetName = selectedProperty.value.propertyName;
  const targetId = selectedProperty.value.propertyId;

  subscribing.value = true;
  error.value = "";

  try {
    await CarClient.subscribeProperty(
      targetName,
      0,
      (value: CarPropertyValue) => {
        // 更新列表中的值
        const index = allProperties.value.findIndex(
          (p) => p.propertyName === targetName
        );
        if (index !== -1) {
          allProperties.value[index].value = value;
        }

        // 如果当前选中的是同一个属性，同步更新
        if (selectedProperty.value?.propertyName === targetName) {
          selectedProperty.value.value = value;
        }

        // 同步 subscribedList
        const subIndex = subscribedList.value.findIndex(
          (p) => p.propertyName === targetName
        );
        if (subIndex !== -1) {
          subscribedList.value[subIndex] = {
            ...subscribedList.value[subIndex],
            value,
            subscribed: true,
          };
        } else {
          subscribedList.value.push({
            propertyName: targetName,
            propertyId: targetId,
            subscribed: true,
            value,
          });
        }

        // if (selectedProperty.value) {
        //   $q.notify({
        //     type: "info",
        //     message: `${selectedProperty.value.propertyName} 值更新: ${formatPropertyValue(value.value)}`,
        //     position: "top",
        //     timeout: 2000,
        //   });
        // }
      }
    );

    const listIndex = allProperties.value.findIndex(
      (p) => p.propertyName === targetName
    );
    if (listIndex !== -1) {
      allProperties.value[listIndex].subscribed = true;
    }
    if (selectedProperty.value?.propertyName === targetName) {
      selectedProperty.value.subscribed = true;
    }

    // 将当前属性加入已订阅列表
    const existedIndex = subscribedList.value.findIndex(
      (p) => p.propertyName === targetName
    );
    const baseItem =
      selectedProperty.value?.propertyName === targetName
        ? selectedProperty.value
        : allProperties.value.find((p) => p.propertyName === targetName);
    if (baseItem) {
      const merged = {
        ...baseItem,
        subscribed: true,
      };
      if (existedIndex !== -1) {
        subscribedList.value[existedIndex] = {
          ...subscribedList.value[existedIndex],
          ...merged,
        };
      } else {
        subscribedList.value.push({ ...merged });
      }
    }

    $q.notify({
      type: "positive",
      message: "订阅成功",
      position: "top",
    });
  } catch (err: any) {
    error.value = err?.message || "订阅失败";
    $q.notify({
      type: "negative",
      message: error.value,
      position: "top",
    });
  } finally {
    subscribing.value = false;
  }
};

const performUnsubscribeCleanup = (propertyName: string) => {
  if (selectedProperty.value?.propertyName === propertyName) {
    selectedProperty.value.subscribed = false;
  }

  const index = allProperties.value.findIndex(
    (p) => p.propertyName === propertyName
  );
  if (index !== -1) {
    allProperties.value[index].subscribed = false;
  }

  subscribedList.value = subscribedList.value.filter(
    (p) => p.propertyName !== propertyName
  );
};

const unsubscribeByName = async (propertyName: string) => {
  if (!isNative) return;

  unsubscribing.value = true;
  unsubscribingFrom.value = propertyName;
  error.value = "";

  try {
    await CarClient.unsubscribeProperty(propertyName);
    performUnsubscribeCleanup(propertyName);

    $q.notify({
      type: "positive",
      message: "取消订阅成功",
      position: "top",
    });
  } catch (err: any) {
    error.value = err?.message || "取消订阅失败";
    $q.notify({
      type: "negative",
      message: error.value,
      position: "top",
    });
  } finally {
    unsubscribing.value = false;
    unsubscribingFrom.value = null;
  }
};

const unsubscribeProperty = async () => {
  if (!selectedProperty.value || !isNative) return;
  await unsubscribeByName(selectedProperty.value.propertyName);
};

const formatPropertyValue = (value: any): string => {
  if (value === null || value === undefined) {
    return "--";
  }
  if (typeof value === "number") {
    if (value % 1 !== 0) {
      return value.toFixed(6);
    }
    return value.toString();
  }
  if (Array.isArray(value)) {
    return `[${value.join(", ")}]`;
  }
  return String(value);
};

// 点击列表项选择属性
const selectProperty = (prop: PropertyItem) => {
  selectedProperty.value = prop;
};

onMounted(() => {
  if (isNative) {
    loadAllProperties();
  }
});
</script>

<style scoped>
/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
